import React from 'react';
import { TableHead, TableRow, TableCell, TableSortLabel, Tooltip, Typography, Stack } from '@mui/material';
import { Box } from '@mui/system';
import { visuallyHidden } from '@mui/utils';
import { InfoOutlined } from '@mui/icons-material';

function PackMonitoringHeader({ 
    tableType, 
    order, 
    orderBy, 
    onRequestSort,
    viewMode = 'standard'
}) {
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    const renderSubheaders = (subheaders) => (
        <Stack 
            direction="row" 
            spacing={0}
            sx={{ 
                justifyContent: 'space-around', 
                marginTop: 1,
                fontSize: '14px'
            }}
        >
            {subheaders.map((subheader) => (
                <Tooltip key={subheader.id} title={subheader.tooltip} arrow placement="top">
                    <Typography 
                        sx={{ 
                            flex: 1, 
                            textAlign: 'center', 
                            fontSize: '14px'
                        }}
                    >
                        {subheader.sortable ? (
                            <TableSortLabel
                                active={orderBy === subheader.id}
                                direction={orderBy === subheader.id ? order : 'asc'}
                                onClick={createSortHandler(subheader.id)}
                            >
                                {subheader.label}
                            </TableSortLabel>
                        ) : subheader.label}
                    </Typography>
                </Tooltip>
            ))}
        </Stack>
    );

    const itemHeaders = [
        { id: 'item_name', label: 'Pack Item', sortable: true, width: '38%', tooltip: '' },
        { 
            id: 'waste_estimate', 
            label: viewMode === 'standard' ? 'Waste Estimate' : 'Waste Estimate Range', 
            sortable: false, 
            width: '45%',
            align: 'center',
            tooltip: 'This represents the % of cases this pack item will be wasted in when incorporating the responses of reviewed physicians. This number can increase as more physicians review their pack dashboard',
            subheaders: viewMode === 'standard' ? [
                { id: 'waste_estimate.low_estimate', label: 'Low', sortable: true, tooltip: '' },
                { id: 'waste_estimate.medium_estimate', label: 'Medium', sortable: true, tooltip: '' },
                { id: 'waste_estimate.high_estimate', label: 'High', sortable: true, tooltip: '' }
            ] : undefined
        },
        { id: 'reviewed_usage_percentage', label: 'Physician Reviewed Pack Usage %', sortable: false, width: '17%', align: 'center', tooltip: 'Total Pack Usage % of Physicians who have reviewed. A high number means the physicians who use the pack the most have provided feedback' },
    ];

    const physicianHeaders = [
        { id: 'last_name', label: 'Physician', sortable: false, width: '19%', tooltip: '' },
        { id: 'specialty', label: 'Specialty', sortable: true, width: '14%', tooltip: '' },
        { id: 'pack_usage', label: 'Physician Pack Usage %', sortable: true, width: '23%', align: 'center', tooltip: 'This physician uses N% of this specific packs annual usage' },
        {
            id: 'review_status',
            label: 'Review Status',
            sortable: false,
            width: '24%',
            tooltip: (
                <Box>
                    <Typography variant="heavy" sx={{ mb: 0.5 }}>
                        Review Status
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                        <li>
                            <Typography variant="normal" component="span">
                                <strong>Reviewed:</strong> Physician has reviewed the contents of this pack and provided feedback.
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="normal" component="span">
                                <strong>Not Reviewed:</strong> Physician has been included in the pack review survey but has not submitted feedback.
                            </Typography>
                        </li>
                        <li>
                            <Typography variant="normal" component="span">
                                <strong>Not Engaged:</strong> Physician has not been included in the pack review survey.
                            </Typography>
                        </li>
                    </Box>
                </Box>
            )
        },
        { id: 'vote', label: 'Physician Response', sortable: false, width: '15%', tooltip: '' },
    ];

    const headers = tableType === 'items' ? itemHeaders : physicianHeaders;

    return (
        <TableHead>
            <TableRow className='tableHeader'>
                {headers.map((header) => (
                    <TableCell
                        key={header.id}
                        sortDirection={orderBy === header.id ? order : false}
                        sx={{ 
                            fontWeight: 'bold',
                            ...(header.width && { width: header.width, maxWidth: header.width }),
                            ...(header.align && { textAlign: header.align }),
                            ...(tableType === 'items' && header.id === 'item_name' && {
                                borderRight: '1px solid',
                                borderColor: 'divider'
                            }),
                            ...(tableType === 'items' && header.id === 'reviewed_usage_percentage' && {
                                borderLeft: '1px solid',
                                borderColor: 'divider'
                            })
                        }}
                    >
                        <Tooltip title={header.tooltip || ''} arrow placement="top">
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="heavy">
                                    {header.sortable ? (
                                        <TableSortLabel
                                            active={orderBy === header.id}
                                            direction={orderBy === header.id ? order : 'asc'}
                                            onClick={createSortHandler(header.id)}
                                        >
                                            {header.label}
                                            {orderBy === header.id ? (
                                                <Box sx={visuallyHidden}>
                                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                                </Box>
                                            ) : null}
                                        </TableSortLabel>
                                    ) : (
                                        header.label
                                    )}
                                </Typography>
                                {header.tooltip && <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />}
                            </Box>
                        </Tooltip>
                        {header.subheaders && renderSubheaders(header.subheaders)}
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

export default PackMonitoringHeader;